import {TemplatesWebFrontendRequest} from "services-comun-ed/modules/services/templates/frontend-web";
import {LazyLoad} from "./lazyload";
import {NoticiasWebFrontendRequest} from "services-comun-ed/modules/services/noticias/frontend-web";
import {ITagKey, QueryFiltro} from "services-comun-ed/modules/services/noticias/query";
import {INoticiaPortadilla} from "services-comun-ed/modules/services/noticias/interface";
import {NativosController} from "./recursos/nativos";
import Slot = googletag.Slot;
import {TwitterController} from "./recursos/twitter";
import {TwitchController} from "./recursos/twitch";
import {TiktokController} from "./recursos/tiktok";
import {YoutubeController} from "./recursos/youtube";
import {BesoccerController} from "./recursos/besoccer";
import {DailymotionController} from "./recursos/dailymotion";
import {InstagramController} from "./recursos/instagram";
import {EstadioController} from "./recursos/estadio";
import {PublicidadController} from "./publicidad";
import {ProgressiveWebAppController} from "./pwa";

declare global {
    interface Window {
        dataLayer: any;
        marfeel: any;
        subdominio: string;
        id_noticia: number;
        tags_categoria: string;
        originalAdSlots: Slot[];
        testHB: string;
    }
}

interface NoticiasRelacionadasData {
    [key: number]: INoticiaPortadilla;
}

interface SendEvent {
    [key: number]: boolean;
}

export class NoticiaController {

    /* INSTANCE */
    public static instance: NoticiaController;
    private resources: HTMLElement[];
    private readonly scrollEvent: any;
    private lazyInstance: LazyLoad;
    private readonly pie: HTMLElement;
    private readonly cuerpo: HTMLElement;
    private limit: number;
    private readonly noticiasRelacionadas: number[];
    private readonly noticiasRelacionadasData: NoticiasRelacionadasData;
    private readonly sendEvent: SendEvent;
    private page: number;
    private readonly margin: number;
    private lock: boolean;
    private noticiaAnterior: number;
    private activeTitle: string;
    private activeUrl: string;

    /* STATIC */
    public static async build(): Promise<void> {
        NoticiaController.instance = new this();
    }

    private constructor() {
        this.activeTitle = "";
        this.activeUrl = "";
        this.resources = [];
        this.limit = 10;
        this.lock = false;
        this.page = 1;
        this.margin = window.device === "pc" ? 107 : 83;
        this.noticiaAnterior = window.id_noticia;
        this.noticiasRelacionadas = [];
        this.noticiasRelacionadas.push(window.id_noticia);
        this.sendEvent = {};
        this.sendEvent[window.id_noticia] = true;
        this.noticiasRelacionadasData = {};
        this.noticiasRelacionadasData[window.id_noticia] = {
            cache: "",
            antetitulo: "",
            contenido: "",
            fecha: "",
            foto: "",
            id: window.id_noticia,
            overlay: "",
            redactor: {
                avatar: "",
                avatar2x: "",
                id: 0,
                apodo: ""
            },
            tiempo: 0,
            titulo: document.title,
            tituloPortadilla: "",
            tituloRrss: "",
            url: window.location.href
        };
        this.pie = document.querySelector(".c-pie") as HTMLElement;
        this.cuerpo = document.querySelector(".c-cuerpo") as HTMLElement;
        this.resources.push(this.pie);
        this.lazyInstance = new LazyLoad();
        this.lazyInstance.setPixels(1000);
        this.scrollEvent = this.lazyPages.bind(this);
        window.originalAdSlots = window.adslots;
        document.addEventListener("scroll", this.scrollEvent);

        if (window.device === 'mv') {
            ProgressiveWebAppController.build()
        }
    }

    protected async lazyPages(): Promise<void> {
        const id = await this.getActiveContainer();

        if (id > 0) {
            const noticiaActiva = this.noticiasRelacionadasData[id];
            if (noticiaActiva.id !== this.noticiaAnterior) {
                await this.changeData(noticiaActiva.url, noticiaActiva.titulo);
                if (!this.sendEvent[noticiaActiva.id]) {
                    this.sendEvent[noticiaActiva.id] = true;
                    await this.sendEventToGA4();
                    await this.sendEventToMarfeel(this.activeUrl);
                }

                await PublicidadController.instance.refreshSlot("div-skyleft", false);
                await PublicidadController.instance.refreshSlot("div-skyright", false);

                this.noticiaAnterior = noticiaActiva.id;
            }
        }

        if (this.limit > 0) {
            let item = await this.lazyInstance.lazy(this.resources);
            if (item !== undefined && !this.lock) {
                this.lock = true;
                await this.getNoticias();
                this.resources = [];
                let id_noticia = this.noticiasRelacionadas[this.noticiasRelacionadas.length === 1 ? this.page++ : this.page];
                const noticia = await NoticiasWebFrontendRequest.resumen(id_noticia);
                this.noticiasRelacionadasData[id_noticia] = noticia;
                TemplatesWebFrontendRequest.noticiaDesarrollo(id_noticia, window.device).then(async (data) => {
                    //console.log("obtener noticia ->",id_noticia)
                    this.resources.push(this.pie);
                    this.limit--;
                    const parser = new DOMParser();
                    const parsedDocument = parser.parseFromString(data.data, "text/html");
                    const contenedor = parsedDocument.body.querySelector(".contenedor-"+id_noticia) as HTMLElement;
                    document.querySelector(".c-cuerpo")?.appendChild(contenedor);

                    if (typeof window.scheduler !== 'undefined' && typeof window.scheduler.postTask === 'function') {
                        window.scheduler.postTask(()=>{ PublicidadController.instance.reLaunch(contenedor, id_noticia, this.page); }, {priority: "user-blocking"});
                        window.scheduler.postTask(()=>{ NativosController.build(id_noticia); }, {priority: "background"});
                        window.scheduler.postTask(()=>{ TwitterController.instance.reLaunch(contenedor); }, {priority: "background"});
                        window.scheduler.postTask(()=> { TwitchController.instance.reLaunch(contenedor); }, {priority: "background"});
                        window.scheduler.postTask(()=>{ TiktokController.instance.reLaunch(contenedor); }, {priority: "background"});
                        window.scheduler.postTask(()=> { YoutubeController.instance.reLaunch(contenedor); }, {priority: "background"});
                        window.scheduler.postTask(() =>{ BesoccerController.instance.reLaunch(contenedor); }, {priority: "background"});
                        window.scheduler.postTask(()=>{ DailymotionController.build() }, {priority: "background"});
                        window.scheduler.postTask(()=>{ InstagramController.instance.reLaunch(contenedor); }, {priority: "background"});
                        window.scheduler.postTask(()=>{ EstadioController.instance.reLaunch(contenedor); }, {priority: "background"});
                    } else {
                        PublicidadController.instance.reLaunch(contenedor, id_noticia, this.page);
                        NativosController.build(id_noticia);
                        TwitterController.instance.reLaunch(contenedor);
                        TwitchController.instance.reLaunch(contenedor);
                        TiktokController.instance.reLaunch(contenedor);
                        YoutubeController.instance.reLaunch(contenedor);
                        BesoccerController.instance.reLaunch(contenedor);
                        DailymotionController.build();
                        InstagramController.instance.reLaunch(contenedor);
                        EstadioController.instance.reLaunch(contenedor);
                    }
                    this.page++;
                    this.activeTitle = noticia.titulo;
                    this.activeUrl = noticia.url;
                    this.lock = false;
                });

                if (this.page === 2 && window.device === 'mv') ProgressiveWebAppController.instance.showInstallPromotion();
            }
        }
    }

    private async getNoticias(): Promise<void> {
        if (this.noticiasRelacionadas.length === this.page) {
            const tagsCategoria: string[] = window.tags_categoria.split(",");
            const tags: ITagKey[] = [];
            for (const tag of tagsCategoria) tags.push({subdominio: window.subdominio, url: tag});

            const query = QueryFiltro.build({
                tags: [tags],
                subdominio: window.subdominio,
                opinion: false,
                directo: false,
                tipologia: "noticia",
                noId: [window.id_noticia],
            })

            const noticias = await NoticiasWebFrontendRequest.portadilla(query, 1, 10, 2);
            //console.log("obtener ids noticia: ",noticias)
            noticias.map((x)=>{ this.noticiasRelacionadas.push(x); });
        }
    }

    private async getActiveContainer(): Promise<number> {
        const nodes = this.cuerpo.querySelectorAll('.contenedor');
        let selectedId = -1;
        for (const [k, node] of Object.entries(nodes)) {
            const htmlContainer = node as HTMLElement;
            const contenedorRect = htmlContainer.getBoundingClientRect();
            const contenedorTop = contenedorRect.top + window.scrollY;
            const contenedorBottom = contenedorRect.bottom + window.scrollY;
            if (window.scrollY+this.margin >= contenedorTop-this.margin && window.scrollY <= contenedorBottom-this.margin) {
                node.classList.forEach((value: string) => {
                    if (value.includes("-")) {
                        selectedId = Number(value.split("-")[1]);
                    } else {
                        selectedId = window.id_noticia;
                    }
                });
                break;
            }
        }
        return selectedId;
    }

    private async changeData(url: string, title: string): Promise<void> {
        document.title = title;
        window.history.pushState({}, "", url);
    }

    private async sendEventToMarfeel(url: string): Promise<void> {
        window.marfeel.cmd.push(['compass', function(compass: any) {
            compass.trackNewPage({url: url, rs: 'infinite scroll'});
        }]);
    }

    private async sendEventToGA4(): Promise<void> {
        window.dataLayer.push({
            event : 'VirtualPageView',
            page_type: "scroll-infinito"
        });
    }

}
